import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import i18n from 'i18next'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  OffenseListResponse,
  PolicyName,
  Offense,
  Policy,
  Policy_KindEnum,
  Policy_StatusEnum,
  Favorite_PageIdEnum,
} from 'src/api/gen/api'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import usePolicyKindsExceptFeeRates from 'src/commons/hooks/usePolicyKindsExceptFeeRates'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { getQueryParamsFromObject } from 'src/commons/hooks/useUrlState'
import { userHasPermission } from 'src/commons/permissions'
import {
  getTemporalRange,
  ITemporalRange,
  stringToZonedDateTime,
  zonedDateTimeToISOString,
} from 'src/commons/temporalHelpers'
import { useOnboardingJoyride } from 'src/Joyride/OnboardingJoyride/OnBoardingJoyrideWrapper'
import { useProviders } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'

import ViolationsListFilterPanel, { IViolationListFilters } from './ListFilterPanel'
import ViolationsTable from './Table'

export function getInitialFilters(policyKinds: Policy_KindEnum[]): IViolationListFilters {
  const range: ITemporalRange = getTemporalRange({ months: 3 }, true)
  return {
    dateRange: range,
    eventTypes: [],
    include_abnormals: false,
    jurisdiction: [],
    policyKind: policyKinds,
    policyId: [],
    provider: [],
    status: [],
    offenseStatus: [],
    vin: '',
    policyStatus: [Policy_StatusEnum.ongoing],
  }
}

export function getPolicyViolationsSearch(
  policy: Pick<Policy, 'id' | 'start_date' | 'end_date' | 'first_version_start_date'>
): string {
  return getQueryParamsFromObject({
    ...getInitialFilters([]),
    dateRange: {
      from: stringToZonedDateTime(policy.first_version_start_date ?? policy.start_date),
      to: policy.end_date ? stringToZonedDateTime(policy.end_date) : undefined,
    },
    policyId: [policy.id],
    policyStatus: [],
  })
}

const Violations = () => {
  const { t } = useTranslation()

  const { activeProviders } = useProviders()

  const [arePoliciesLoading, setArePoliciesLoading] = useState(true)
  const [areViolationsLoading, setAreViolationsLoading] = useState(true)
  const [policies, setPolicies] = useState<PolicyName[]>([])
  const [violations, setViolations] = useState<Offense[]>([])
  const [areViolationsTruncated, setAreViolationsTruncated] = useState<boolean>(false)
  const policyKinds = usePolicyKindsExceptFeeRates().map((kind) => kind.id)
  const defaultFilters = useMemo(() => getInitialFilters(policyKinds), [policyKinds])
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.policies_violations, defaultFilters)
  const displayedFiltersRef = useRef<IViolationListFilters>()
  const { resumeJoyride } = useOnboardingJoyride()

  useEffect(() => {
    setArePoliciesLoading(true)

    api.policies
      .policiesNames({ kind: policyKinds })
      .then(setPolicies)
      .catch(console.error)
      .finally(() => {
        setArePoliciesLoading(false)
      })
  }, [])

  const fetchViolations = async function (signal: AbortSignal) {
    setAreViolationsLoading(true)
    try {
      await api.offenses
        .offenseList({
          include_abnormals: filters.include_abnormals,
          end_date_after: filters.dateRange.from ? zonedDateTimeToISOString(filters.dateRange.from) : undefined,
          policy_ids: filters.policyId,
          policy_statuses:
            filters.policyStatus.length > 0
              ? filters.policyStatus
              : [Policy_StatusEnum.ongoing, Policy_StatusEnum.finished],
          policy_types: filters.policyKind,
          provider_ids: filters.provider,
          start_date_before: filters.dateRange.to ? zonedDateTimeToISOString(filters.dateRange.to) : undefined,
          vin: filters.vin,
          status: filters.status,
          event_types: filters.eventTypes,
          offense_status: filters.offenseStatus,
          jurisdiction_keys: filters.jurisdiction,
          options: { signal },
        })
        .then((response: OffenseListResponse) => {
          setViolations(response.results)
          setAreViolationsTruncated(response.truncated)
        })
    } catch (error) {
      if (!signal.aborted) {
        console.error(error)
      }
    } finally {
      setAreViolationsLoading(false)
      //resule joyride tutorial if halted
      resumeJoyride()
    }
  }

  function onVinChange(input: string) {
    setFilters((pendingFilters) => ({ ...pendingFilters, vin: input }))
  }

  let onClickExport = undefined
  if (userHasPermission(['analyze', 'policy'])) {
    onClickExport = async function () {
      await api.offenses.exportOffenseList({
        include_abnormals: filters.include_abnormals,
        vin: filters.vin,
        start_date_before: filters.dateRange.to ? zonedDateTimeToISOString(filters.dateRange.to) : undefined,
        end_date_after: filters.dateRange.from ? zonedDateTimeToISOString(filters.dateRange.from) : undefined,
        event_types: filters.eventTypes,
        policy_ids: filters.policyId,
        policy_statuses:
          filters.policyStatus.length > 0
            ? filters.policyStatus
            : [Policy_StatusEnum.ongoing, Policy_StatusEnum.finished],
        policy_types: filters.policyKind,
        provider_ids: filters.provider,
        status: filters.status,
        offense_status: filters.offenseStatus,
        jurisdiction_keys: filters.jurisdiction,
        lang: i18n.language,
      })
    }
  }

  function onClickFilter(_event: React.MouseEvent<HTMLElement>): void {
    setFilters((pendingFilters) => ({ ...pendingFilters, include_abnormals: !pendingFilters.include_abnormals }))
  }

  function onApplyFilterClick() {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters(displayedFiltersRef.current)
  }

  useDeepCompareEffect(() => {
    const abortController = new AbortController()
    fetchViolations(abortController.signal)
    return () => abortController.abort()
  }, [filters])

  return (
    <FilteringCard
      SideComponent={
        <ViolationsListFilterPanel
          ref={displayedFiltersRef}
          initialFilters={filters}
          defaultFilters={defaultFilters}
          providers={activeProviders}
          policies={policies}
        />
      }
      onApplyFilter={onApplyFilterClick}
    >
      <ViolationsTable
        data={violations}
        isLoading={arePoliciesLoading || areViolationsLoading}
        truncated={areViolationsTruncated}
        onClickExport={onClickExport}
        filter={{
          onClick: onClickFilter,
          title: (filters.include_abnormals
            ? t('common.exclude_abnormals_offenses')
            : t('common.include_abnormals_offenses')) as string,
          icon: filters.include_abnormals ? RemoveCircleOutlineIcon : AddCircleOutlineIcon,
        }}
        searchBar={{
          value: filters.vin,
          onChange: onVinChange,
          placeholder: t('pricing.vin'),
        }}
      />
    </FilteringCard>
  )
}

export default Violations
